import api from './api';

const userService = {
  /**
   * Get all users with optional pagination and filters
   * @param {Object} options - Options for fetching users
   * @param {number} options.page - Page number (1-based)
   * @param {number} options.limit - Number of items per page
   * @param {Object} options.filters - Filter criteria
   * @param {string} [options.filters.email] - Filter by email (case-insensitive)
   * @param {string} [options.filters.full_name] - Filter by full name (case-insensitive)
   * @param {string} [options.filters.role] - Filter by user role
   * @param {boolean} [options.filters.is_active] - Filter by active status
   * @returns {Promise<{data: Array, pagination: {page: number, limit: number, total: number}}>}
   */
  async getUsers({ page = 1, limit = 10, filters = {} } = {}) {
    try {
      // Prepare query parameters
      const params = {
        skip: (page - 1) * limit,
        limit,
        ...filters
      };

      // Clean up params
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === null || params[key] === '') {
          delete params[key];
        }
      });
      
      const response = await api.get('/users/', { 
        params,
        headers: {
          'X-Total-Count': 'true',
          'Accept': 'application/json'
        }
      });

      // Get total count from headers if available
      const total = response.headers['x-total-count'] 
        ? parseInt(response.headers['x-total-count'], 10) 
        : response.data.length;

      return {
        data: response.data,
        pagination: {
          page: parseInt(page, 10),
          limit: parseInt(limit, 10),
          total: total,
          totalPages: Math.ceil(total / limit)
        }
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  /**
   * Get a single user by ID
   * @param {number|string} id - The ID of the user to fetch
   * @returns {Promise<Object>} The user data
   */
  async getUser(id) {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching user with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new user
   * @param {Object} userData - The user data to create
   * @returns {Promise<Object>} The created user data
   */
  async createUser(userData) {
    try {
      
      const response = await api.post('/users/', userData);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  /**
   * Update an existing user
   * @param {number|string} id - The ID of the user to update
   * @param {Object} userData - The updated user data
   * @returns {Promise<Object>} The updated user data
   */
  async updateUser(id, userData) {
    try {
      const response = await api.put(`/users/${id}`, userData);
      return response.data;
    } catch (error) {
      console.error(`Error updating user with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a user
   * @param {number|string} id - The ID of the user to delete
   * @returns {Promise<void>}
   */
  async deleteUser(id) {
    try {
      await api.delete(`/users/${id}`);
    } catch (error) {
      console.error(`Error deleting user with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Toggle user active status
   * @param {number|string} id - The ID of the user
   * @param {boolean} isActive - The new active status
   * @returns {Promise<Object>} The updated user data
   */
  async toggleUserStatus(id, isActive) {
    try {
      const response = await api.put(`/users/${id}/status`, { is_active: isActive });
      return response.data;
    } catch (error) {
      console.error(`Error toggling status for user with ID ${id}:`, error);
      throw error;
    }
  }
};

export default userService;
